import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { isEqual } from "lodash";

// Material UI Components
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Badge from "@mui/material/Badge";

import Button from "components/buttons/Button/Button.component";
import Header from "components/menus/Header/Header.component";
import FilterMenu from "features/inventory/components/FilterMenu/FilterMenu.component";
import ListingCard from "components/cards/ListingCard/ListingCard.component";
import Footer from "components/menus/Footer/Footer.component";
import InventoryTitle from "features/inventory/components/InventoryTitle/InventoryTitle.component";
import InventorySortDropdown from "features/inventory/components/InventorySortDropdown/InventorySortDropdown.component";
import ContentBoxes from "features/inventory/components/ContentBoxes/ContentBoxes.component";
import Label from "components/typographies/Label/Label.component";
import Heading from "components/typographies/Heading/Heading.component";
import LocationPopup from "features/inventory/components/LocationPopup/LocationPopup.component";
import FilterModalButton from "features/inventory/components/FilterModalButton/FilterModalButton.component";

import useInventoryFilters from "hooks/inventory/useInventoryFilters.hook";
import {
  useGetInventoryCountQuery,
  useGetInventoryQuery,
} from "services/v1-slices/inventory-slice";
import { convertFiltersForApi } from "helpers/inventory";
import { boundingBoxAroundPoint } from "helpers/distance-helpers";
import useQueryParamsState from "hooks/useQueryParamsState.hook";
import { vdpRoute } from "routes";
import { getGooglePlaceFromLocationName } from "helpers/google-helpers";
import { modalReducer } from "reducers/modal-reducer";
import { inventoryReducer } from "reducers/inventory-reducer";
import usePageWidth from "hooks/usePageWidth.hook";
import { useGetVehicleSpecsQuery } from "services/v1-slices/vehicle-specs-slice";

// Styles
import theme from "theme";
import { useStyles } from "./Inventory.styles";

const Inventory = ({ location, specs }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [listings, setListings] = useState([]);
  const [page, setPage] = useState(0);
  const [loc, setLoc] = useState({});
  const [radiusPopoverOpen, setRadiusPopoverOpen] = useState(false);
  const [radiusDrawerOpen, setRadiusDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [queryRadius, setRadius] = useQueryParamsState("radius", 100);
  const radius = Number(queryRadius);
  const [provinceOnly, setProvinceOnly] = useQueryParamsState(
    "provinceOnly",
    false,
  );
  const { filters: inventoryFilters, filterCount } = useInventoryFilters();
  const { data: attrData, isSuccess: isAttrSuccess } =
    useGetVehicleSpecsQuery();
  // todo: fix the way we handle attributes so we don't need to map plural to singular
  const specsData = isAttrSuccess ? attrData : specs.data;
  const attributes = isAttrSuccess
    ? {
        make: specsData.makes,
        model: specsData.models,
        bodyType: specsData.bodyTypes,
        exteriorColour: specsData.colours,
        interiorColour: specsData.colours,
        transmission: specsData.transmissions,
        fuelType: specsData.fuelTypes,
        driveType: specsData.driveTypes,
        lifestyle: specsData.lifestyles,
      }
    : {};
  const userLocationFromReducer = useSelector((s) => s.inventory.userLocation);
  const { isDesktopWidth } = usePageWidth();

  const userLocation = userLocationFromReducer || location;
  const boundingBox = boundingBoxAroundPoint(
    userLocation?.lat,
    userLocation?.lng,
    radius,
  );
  // cant change limit to be based on width because limit * page is listing offset, changing will
  // give duplicated data
  const limit = 12;
  const expectedListingsSize = limit * (page + 1);
  const inventoryQueryParams = isAttrSuccess
    ? {
        status: "published",
        ...convertFiltersForApi(inventoryFilters, attributes),
        latitude:
          radius && boundingBox
            ? `${boundingBox.lat[0]}..${boundingBox.lat[1]}`
            : undefined,
        longitude:
          radius && boundingBox
            ? `${boundingBox.lng[0]}..${boundingBox.lng[1]}`
            : undefined,
        limit,
        page,
        city: !radius ? userLocation?.city : undefined,
        province:
          provinceOnly || !radius
            ? userLocation?.province?.toUpperCase()
            : undefined,
      }
    : {};

  // If radius is selected, we need to have a bounding box else we need to have a city + prov
  const skipFetchingInventory =
    (radius && !boundingBox) || !(userLocation.city || userLocation.province);

  const { data, isFetching, isSuccess } = useGetInventoryQuery(
    inventoryQueryParams,
    {
      skip: skipFetchingInventory,
    },
  );
  const offset = page * limit;
  const isFetchingListings =
    isFetching || !isEqual(data?.data, listings.slice(offset, offset + limit));
  // This fetches the next page of results to make loading seem faster
  useGetInventoryQuery(
    { ...inventoryQueryParams, page: page + 1 },
    { skip: skipFetchingInventory },
  );

  // We don't care what page we are on for the count
  const { data: listingsCount, isFetching: isFetchingCount } =
    useGetInventoryCountQuery(
      { ...inventoryQueryParams, page: 0 },
      { skip: skipFetchingInventory },
    );

  const endOfResults = listings.length >= listingsCount?.totalCount;

  const clearInventoryCards = () => {
    setPage(0);
  };

  const handleTalkToVE = () => {
    dispatch(
      modalReducer.actions.show({ modal: "talkToVE", variant: "default" }),
    );
  };

  const handleLocationClose = () => {
    setRadiusDrawerOpen(false);
    setRadiusPopoverOpen(false);
  };

  const handleLocationButtonClick = () => {
    if (isDesktopWidth) {
      setRadiusPopoverOpen(true);
    } else {
      setRadiusDrawerOpen(true);
    }
  };

  const renderNoResults = () => (
    <div className={classes.noResults}>
      <Heading variant="h5">No exact matches.</Heading>
      <Label size="lg">
        {filterCount
          ? "Adjust your filters to continue searching or get help from a vehicle expert."
          : "Change your location or expand your radius, or get help from a vehicle expert."}
      </Label>
      <div className={classes.noResultsButtonsContainer}>
        {filterCount ? (
          <Button
            variant="outlined"
            onClick={() =>
              dispatch(inventoryReducer.actions.setFilterModalOpen(true))
            }
            endIcon={
              <Badge
                classes={{ badge: classes.customBlackBadge }}
                className={classes.filterCountBadge}
                badgeContent={filterCount}
              />
            }
          >
            Adjust Filters
          </Button>
        ) : (
          <Button variant="outlined" onClick={handleLocationButtonClick}>
            Change location
          </Button>
        )}
        <Button variant="contained" onClick={handleTalkToVE}>
          Get help
        </Button>
      </div>
    </div>
  );

  useEffect(() => {
    if (!isFetching && isSuccess) {
      if (page === 0) {
        setListings(data?.data);
      } else if (
        listings.length < expectedListingsSize &&
        listings.length < listingsCount?.totalCount &&
        !endOfResults
      ) {
        setListings((previousState) => previousState.concat(data?.data));
      }
    }
  }, [inventoryFilters, page, data?.data]);

  useEffect(() => {
    getGooglePlaceFromLocationName(location, setLoc);
  }, []);

  useEffect(() => {
    dispatch({
      type: "inventory/setUserLocation",
      payload: loc,
    });
  }, [loc]);

  return (
    <div className={classes.root} data-filter-count={filterCount}>
      <Header linkColour="black" noPadding noBorder />
      <Divider sx={{ borderRadius: 1, borderColor: theme.palette.border }} />
      <div className={classes.secondHeader}>
        <FilterMenu
          listingsCount={listingsCount?.totalCount}
          attributes={attributes}
          clearInventoryCards={clearInventoryCards}
          locationFormProps={{
            radius,
            provinceOnly,
            setRadius,
            setProvinceOnly,
            location,
          }}
        />
        <div className={classes.firstFilterRow}>
          <InventoryTitle
            handleClick={handleLocationButtonClick}
            userLocation={userLocation}
            radius={radius}
            noLocation
            setAnchorEl={setAnchorEl}
          />
          <div className="flexGrow" />
          <span className={classes.desktopOnly}>
            <InventorySortDropdown
              dataCy="inventory-sort-type"
              clearInventoryCards={clearInventoryCards}
            />
          </span>
        </div>
      </div>
      {isDesktopWidth && (
        <Divider sx={{ borderRadius: 1, borderColor: theme.palette.border }} />
      )}
      <div className={classes.inventoryContainer}>
        <div className={classes.listingsContainer}>
          {!isDesktopWidth && (
            <FilterModalButton
              buttonLabel={isDesktopWidth ? "Filter" : "Sort & Filter"}
            />
          )}
          <Grid
            container
            rowSpacing="40px"
            columnSpacing="24px"
            sx={{ paddingBottom: "50px" }}
          >
            {!isFetchingListings &&
              listings.map((listing) => (
                <Grid
                  key={listing.id}
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  xxl={2}
                >
                  <ListingCard
                    dataCy="vehicle-card"
                    listing={listing}
                    href={{
                      pathname: vdpRoute,
                      query: {
                        year: listing.vehicle.year,
                        make: listing.vehicle.model.make.name,
                        model: listing.vehicle.model.name,
                        city: listing.address.city,
                        vehicleId: listing.vehicle.id,
                      },
                    }}
                  />
                </Grid>
              ))}
            {isSuccess &&
              !isFetchingListings &&
              listings?.length === 0 &&
              renderNoResults()}
            {!userLocation && (
              <Grid item xs={12}>
                <Typography>
                  Please select a location to get started.
                </Typography>
              </Grid>
            )}
            {isFetchingListings && (
              <div className={classes.loadingSpinner}>
                <CircularProgress data-cy="inventory-loader" />
                <Heading variant="h5">Loading...</Heading>
              </div>
            )}
          </Grid>
        </div>
        {!isFetchingListings && listings?.length > 0 && (
          <div className={classes.showMoreContainer}>
            {/* if page is 0 and we are fetching count, totalCount is undefined */}
            <Label
              size="md"
              weight="light"
              color={theme.palette.content.secondary}
            >
              {isFetchingCount
                ? "Loading..."
                : `Showing ${listings.length} of ${listingsCount?.totalCount}`}
            </Label>
            <LinearProgress
              color="dark"
              variant="determinate"
              value={(listings.length / listingsCount?.totalCount) * 100}
            />
            <Button
              variant="outlined"
              size="md"
              onClick={() => setPage(page + 1)}
              disabled={endOfResults}
            >
              Show more
            </Button>
          </div>
        )}
        <ContentBoxes />
        <div className={classes.letsChatButtonContainer}>
          <Button
            onClick={handleTalkToVE}
            variant="tertiary"
            size="md"
            className={classes.letsChatButton}
          >
            Let&apos;s chat
          </Button>
        </div>
      </div>
      <Footer />
      <LocationPopup
        clearInventoryCards={clearInventoryCards}
        popoverOpen={radiusPopoverOpen}
        drawerOpen={radiusDrawerOpen}
        handleClose={handleLocationClose}
        anchorEl={anchorEl}
        listingsCount={listingsCount?.totalCount}
        locationFormProps={{
          radius,
          provinceOnly,
          setRadius,
          setProvinceOnly,
          location,
        }}
      />
    </div>
  );
};

export default Inventory;
